import { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import ReactSelect from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { tagManagerPersist } from '../../../../api'
import * as PersonFormFields from '../../../../common/person/personformfields'
import {
	countryFilterOption,
	creatableSelectTagSettings,
	reactSelectSearchSettings,
} from '../../../../common/reactselect'
import { fetchTags, localized } from '../../../../common/utils'
import { personNumberSelector } from '../../../../reducers'

export function PersonalDetails({
	person,
	setPerson,
	handlePerson,
	handleDetails,
	handleOtherDetails,
	handleControlDetails,
	selectChange,
	countryOptions,
	fieldParams,
	formErrors,
	isPersonal,
}) {
	const { t } = useTranslation()

	const nextPersonNumber = useSelector(personNumberSelector)

	const [tagOptions, setTagOptions] = useState([])

	const handleTags = (options) =>
		setPerson({
			...person,
			tags: options.map((option) => ({ codeItemId: option.value, codeKey: option.label })),
		})

	const handleCreateTag = async (value) => {
		const res = await tagManagerPersist([{ codeKey: value }])
		const { codeItemId, codeKey } = res.data[0]
		setTagOptions([...tagOptions, { value: codeItemId, label: codeKey }])
		setPerson({
			...person,
			tags: [...person.tags, { codeItemId: codeItemId, codeKey: codeKey }],
		})
	}

	// fetch code lists
	useEffect(() => {
		fetchTags(setTagOptions)
	}, [])

	return (
		<>
			<Row className="mb-3">
				<Form.Group as={Col} xl={2} controlId="nr">
					<PersonFormFields.Nr
						onChange={handlePerson}
						defaultValue={person.nr ?? nextPersonNumber}
						formErrors={formErrors}
						disabled={person.personId}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={4} controlId="forename">
					<PersonFormFields.Forename
						onChange={handlePerson}
						defaultValue={person.forename}
						formErrors={formErrors}
						surname={person.surname}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={4} controlId="surname">
					<PersonFormFields.Surname
						onChange={handlePerson}
						defaultValue={person.surname}
						formErrors={formErrors}
						forename={person.forename}
					/>
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={4} controlId="callingName">
					<PersonFormFields.CallingName
						onChange={handlePerson}
						defaultValue={person.callingName}
						formErrors={formErrors}
						disabled={isPersonal}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={6} controlId="name">
					<PersonFormFields.Name disabled defaultValue={person.name} formErrors={formErrors} />
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={4} controlId="socSec">
					<PersonFormFields.IdentifierCode
						onChange={handlePerson}
						defaultValue={person.socSec}
						formErrors={formErrors}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={4} controlId="gender">
					<PersonFormFields.IdentifierCodeType
						onChange={handlePerson}
						defaultValue={person.gender}
						formErrors={formErrors}
						disabled={isPersonal}
					/>
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={12} controlId="line1">
					<hr></hr>
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={4} controlId="email">
					<Form.Label>{t('EmailWork', 'Sähköposti työ')}</Form.Label>
					<Form.Control
						type="email"
						onChange={handleOtherDetails}
						defaultValue={person.otherDetails?.email}
						isInvalid={!!formErrors.email}
						maxLength={fieldParams.email?.maxLength}
					/>
					<Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
				</Form.Group>

				<Form.Group as={Col} xl={4} controlId="email2">
					<Form.Label>{t('EmailHome', 'Sähköposti oma')}</Form.Label>
					<Form.Control
						type="email"
						onChange={handleOtherDetails}
						defaultValue={person.otherDetails?.email2}
						isInvalid={!!formErrors.email2}
						maxLength={fieldParams.email2?.maxLength}
					/>
					<Form.Control.Feedback type="invalid">{formErrors.email2}</Form.Control.Feedback>
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={4} controlId="tel">
					<PersonFormFields.Tel onChange={handleDetails} defaultValue={person.details?.tel} formErrors={formErrors} />
				</Form.Group>

				<Form.Group as={Col} xl={4} controlId="tel2">
					<PersonFormFields.Tel2 onChange={handleDetails} defaultValue={person.details?.tel2} formErrors={formErrors} />
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={3} controlId="streetAddress">
					<PersonFormFields.StreetAddress
						onChange={handleDetails}
						defaultValue={person.details?.streetAddress}
						formErrors={formErrors}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={2} controlId="postalCode">
					<PersonFormFields.PostalCode
						onChange={handleDetails}
						defaultValue={person.details?.postalCode}
						formErrors={formErrors}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={3} controlId="postalAddress">
					<PersonFormFields.PostalAddress
						onChange={handleDetails}
						defaultValue={person.details?.postalAddress}
						formErrors={formErrors}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={2} controlId="country">
					<Form.Label>{t('CountryCode', 'Maakoodi')}</Form.Label>
					<ReactSelect
						name="country"
						inputId="country"
						onChange={selectChange}
						options={countryOptions}
						value={countryOptions.find((option) => option.value === person.details?.country)}
						{...reactSelectSearchSettings(t)}
						filterOption={countryFilterOption}
					/>
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={12} controlId="line2">
					<hr></hr>
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={4} controlId="iban">
					<PersonFormFields.IBAN onChange={handleDetails} defaultValue={person.details?.iban} formErrors={formErrors} />
				</Form.Group>

				<Form.Group as={Col} xl={4} controlId="bic">
					<PersonFormFields.BIC
						onChange={handleDetails}
						defaultValue={person.details?.bic}
						formErrors={formErrors}
						jsonProperty="details.bic"
					/>
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={4} controlId="language">
					<Form.Label>{t('Language', 'Kieli')}</Form.Label>
					<Form.Select
						key={`language-${person.personId ?? 'new'}`}
						defaultValue={person.controlDetails?.language}
						onChange={handleControlDetails}
					>
						{localized.languageOptions}
					</Form.Select>
					<Form.Control.Feedback type="invalid">{formErrors.language}</Form.Control.Feedback>
				</Form.Group>

				<Form.Group as={Col} xl={4} controlId="payslipMethod">
					<Form.Label>{t('PayslipMethod', 'Laskelman lähetystapa')}</Form.Label>
					<Form.Select
						key={`payslipMethod-${person.personId ?? 'new'}`}
						onChange={handleControlDetails}
						defaultValue={person.controlDetails?.payslipMethod}
						disabled={isPersonal}
					>
						{/* TODO these values need to be predefined constants */}
						<option />
						<option value="XML">{t('Esalary', 'Verkkopalkka')}</option>
						<option value="Tuloste">{t('Printout', 'Tuloste')}</option>
						<option value="Sähköposti">{t('Email', 'Sähköposti')}</option>
					</Form.Select>
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={4} controlId="taxNumber">
					<Form.Label>{t('TaxNumber', 'Veronumero')}</Form.Label>
					<Form.Control onChange={handleControlDetails} defaultValue={person.controlDetails?.taxNumber} />
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={12} controlId="line3">
					<hr></hr>
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={4} controlId="nationality">
					<Form.Label>{t('Nationality', 'Kansalaisuus')}</Form.Label>
					<ReactSelect
						name="nationality"
						inputId="nationality"
						onChange={selectChange}
						options={countryOptions}
						value={countryOptions.find((option) => option.value === person.nationalDetails?.nationality)}
						{...reactSelectSearchSettings(t)}
					/>
					<Form.Control.Feedback type="invalid">{formErrors.nationality}</Form.Control.Feedback>
				</Form.Group>

				<Form.Group as={Col} xl={4} controlId="nationalitySecondary">
					<Form.Label>{t('SecondaryNationality', 'Toinen kansalaisuus')}</Form.Label>
					<ReactSelect
						name="nationalitySecondary"
						inputId="nationalitySecondary"
						onChange={selectChange}
						options={countryOptions}
						isClearable
						value={countryOptions.find((option) => option.value === person.nationalDetails?.nationalitySecondary)}
						{...reactSelectSearchSettings(t)}
					/>
					<Form.Control.Feedback type="invalid">{formErrors.nationalitySecondary}</Form.Control.Feedback>
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={12} controlId="line4">
					<hr></hr>
				</Form.Group>
			</Row>

			<Row className="mb-3">
				<Form.Group as={Col} xl={10} controlId="closeRelatives">
					<Form.Label>{t('CloseRelatives', 'Lähiomainen')}</Form.Label>
					<Form.Control
						placeholder={t('CloseRelativesPlaceholder', 'Nimi, yhteystiedot')}
						type="closeRelatives"
						onChange={handleOtherDetails}
						defaultValue={person.otherDetails?.closeRelatives}
						isInvalid={!!formErrors.closeRelatives}
						maxLength={fieldParams.closeRelatives?.maxLength}
					/>
					<Form.Control.Feedback type="invalid">{formErrors.closeRelatives}</Form.Control.Feedback>
				</Form.Group>
			</Row>

			<Row className="mb-1">
				<Form.Group as={Col} controlId="personkeywords">
					<Form.Label>{t('Keywords', 'Avainsanat')}</Form.Label>
					<CreatableSelect
						inputId="personkeywords"
						value={person?.tags?.map((tag) => ({ value: tag.codeItemId, label: tag.codeKey }))}
						onChange={handleTags}
						onCreateOption={handleCreateTag}
						options={tagOptions}
						{...creatableSelectTagSettings(t)}
					/>
				</Form.Group>
			</Row>
		</>
	)
}
